import { useCallback, useEffect, useState } from 'react';

/**
 * MSG91's hosted OTP widget.
 *
 * MSG91 sends the code on the channel the widget is configured for, collects it
 * in its own modal and hands us an access-token. We never see the code — the
 * backend posts the token to MSG91 to learn which phone number was verified.
 * That means no DLT template to maintain and no OTP of ours in a database.
 *
 * Both values below are client-side by design — MSG91 ships them in the page
 * for every visitor, so they are defaults in the source rather than secrets in
 * an env var. The key that *verifies* tokens (MSG91_AUTH_KEY) is a real secret
 * and lives only on the backend. Env vars still win, so a second environment
 * can point at its own widget without a code change.
 *
 * MSG91 account "buy9", widget "BuySellTaxi" — BST's own widget, separate from
 * the one Infinite Carz uses, so its settings and its SMS template can change
 * without touching that product. Client token "bstweb" is likewise BST-only and
 * can be revoked on its own.
 *
 * Widget config: mobile number only (the backend resolves a phone, so an email
 * identifier would fail verification), 6-digit code, SMS primary with a Voice
 * retry, captcha on, India-only. WhatsApp is offered by MSG91 but is not
 * switched on — do not promise it in the UI copy.
 */
const WIDGET_ID = process.env.REACT_APP_MSG91_WIDGET_ID || '3668676f756f383838313934';
const TOKEN_AUTH = process.env.REACT_APP_MSG91_TOKEN_AUTH || '517730TXk2HdOH86a75f9a8P1';

export const isMsg91Configured = () => !!(WIDGET_ID && TOKEN_AUTH);

// verify.msg91.com is blocked by some Indian ISPs and corporate DNS; phone91 is
// MSG91's own mirror and is worth the second attempt before giving up.
const SCRIPT_URLS = [
  'https://verify.msg91.com/otp-provider.js',
  'https://verify.phone91.com/otp-provider.js',
];

// One load per page, shared by every hook instance.
let scriptLoading = null;

function loadWidgetScript() {
  if (typeof window === 'undefined') return Promise.reject(new Error('No window'));
  if (typeof window.initSendOTP === 'function') return Promise.resolve();
  if (scriptLoading) return scriptLoading;

  scriptLoading = new Promise((resolve, reject) => {
    let index = 0;
    const attempt = () => {
      const script = document.createElement('script');
      script.src = SCRIPT_URLS[index];
      script.async = true;
      script.onload = () => {
        if (typeof window.initSendOTP === 'function') resolve();
        else reject(new Error('OTP widget loaded but initSendOTP is missing'));
      };
      script.onerror = () => {
        index += 1;
        if (index < SCRIPT_URLS.length) attempt();
        else reject(new Error('Could not load the OTP widget'));
      };
      document.head.appendChild(script);
    };
    attempt();
  });
  return scriptLoading;
}

/**
 * @returns {{launch: Function, ready: boolean, loading: boolean, failed: boolean}}
 *   `failed` is what the login pages watch to show the fallback form — a
 *   blocked script must not leave someone staring at a dead button.
 */
export function useMsg91Otp() {
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(isMsg91Configured());
  const [failed, setFailed] = useState(!isMsg91Configured());

  useEffect(() => {
    if (!isMsg91Configured()) return undefined;
    let cancelled = false;
    loadWidgetScript()
      .then(() => { if (!cancelled) setReady(true); })
      .catch((e) => {
        console.error('[MSG91] widget script failed:', e.message);
        if (!cancelled) setFailed(true);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const launch = useCallback(({ identifier, onSuccess, onFailure }) => {
    if (!ready || typeof window.initSendOTP !== 'function') {
      onFailure?.(new Error('OTP service is not ready yet'));
      return;
    }
    window.initSendOTP({
      widgetId: WIDGET_ID,
      tokenAuth: TOKEN_AUTH,
      identifier: identifier || '',
      exposeMethods: false,
      // The token arrives as a bare string on some widget versions and as
      // { message } on others.
      success: (data) => onSuccess(typeof data === 'string' ? data : data?.message || String(data)),
      failure: (err) => {
        console.warn('[MSG91] OTP cancelled or failed:', err);
        onFailure?.(err);
      },
    });
  }, [ready]);

  return { launch, ready, loading, failed };
}
